import type { Locale } from 'date-fns'
import { formatDistanceToNow } from 'date-fns/formatDistanceToNow'
import { intervalToDuration } from 'date-fns/intervalToDuration'
import { enUS } from 'date-fns/locale/en-US'
import { ru } from 'date-fns/locale/ru'
import { defaultDateStyle, defaultLocaleForDate, defaultTimeZone } from '@/config/date'

/**
 * Maps a BCP 47 locale tag (same family as `Intl` / `defaultLocaleForDate`) to a
 * date-fns `Locale` for `formatDistanceToNow`. date-fns does not accept raw
 * strings; without this, relative phrases would always follow English rules.
 * Unknown languages fall back to `enUS` so distance strings stay consistent
 * with typical `en-*` formatting when we have no bundled date-fns locale.
 */
function dateFnsLocaleFor(bcp47: string): Locale {
  const language = bcp47.split(/[-_]/)[0]?.toLowerCase() ?? 'en'
  if (language === 'ru') return ru
  return enUS
}

export interface StartDateOptions {
  // Inputs for formatStartDate().

  date: Date | string
  // Start date as Date or ISO string.

  locale?: string
  // Locale for formatting (e.g. en-US). Default from config.

  dateStyle?: 'long' | 'short' | 'medium'
  // Intl dateStyle. Default from config.

  timeZone?: string
  // IANA timezone for display. Undefined = local. Default from config.
}

export interface StartDateDurationParts {
  // Breakdown from date-fns intervalToDuration between min(start, now) and max(start, now); missing units treated as 0.

  years: number
  // Years component.

  months: number
  // Months component.

  days: number
  // Days component.
}

export interface StartDateResult {
  // Formatted start date plus elapsed time since then.

  formattedDate: string
  // Date string in the given locale and timezone.

  durationSince: string
  // Human-readable time since the date (e.g. date-fns "about 5 hours ago", "3 years ago").

  duration: StartDateDurationParts
  // Numeric duration parts for custom UI.
}

/**
 * Normalize input to a Date in local time (for "today" and for the start date we compare in local time).
 */
function toDate(date: Date | string): Date {
  return typeof date === 'string' ? new Date(date) : date
}

/**
 * Convenience wrapper for API ISO strings. Returns `{ formattedDate, durationSince }`
 * with project defaults (UTC, medium dateStyle, defaultLocaleForDate).
 * Returns empty strings for null, undefined, blank, or unparseable input.
 */
export function formatStartDateFromIso(
  iso: string | null | undefined,
): Pick<StartDateResult, 'formattedDate' | 'durationSince'> {
  if (typeof iso !== 'string' || iso.trim() === '') {
    return { formattedDate: '', durationSince: '' }
  }
  const { formattedDate, durationSince } = formatStartDate({
    date: iso,
    locale: defaultLocaleForDate.value,
    dateStyle: 'medium',
    timeZone: 'UTC',
  })
  return { formattedDate, durationSince }
}

/**
 * Format a start date in locale and timezone, and compute time since that date.
 * Date is displayed in the configured (or local) timezone.
 */
export function formatStartDate(options: StartDateOptions): StartDateResult {
  const {
    date,
    locale = defaultLocaleForDate.value,
    dateStyle = defaultDateStyle,
    timeZone = defaultTimeZone,
  } = options

  const start = toDate(date)
  const end = new Date()

  if (Number.isNaN(start.getTime())) {
    return {
      formattedDate: '',
      durationSince: '',
      duration: { years: 0, months: 0, days: 0 },
    }
  }

  const formattedDate = new Intl.DateTimeFormat(locale, {
    timeZone: timeZone ?? undefined,
    dateStyle,
  }).format(start)

  const earlier = start.getTime() <= end.getTime() ? start : end
  const later = start.getTime() <= end.getTime() ? end : start
  const duration = intervalToDuration({ start: earlier, end: later })
  const years = duration.years ?? 0
  const months = duration.months ?? 0
  const days = duration.days ?? 0

  const durationSince = formatDistanceToNow(start, {
    addSuffix: true,
    locale: dateFnsLocaleFor(locale),
  })

  return {
    formattedDate,
    durationSince,
    duration: { years, months, days },
  }
}

/**
 * Compact "since" label (`3y2m`, `6m`, `5yr`) built from `formatStartDate`'s
 * duration parts instead of its verbose "3 years ago" string — for
 * space-constrained UI like a mobile roster row.
 */
export function compactSince(startDatetime: string | null | undefined): string | null {
  if (typeof startDatetime !== 'string' || startDatetime.trim() === '') return null
  const start = new Date(startDatetime)
  if (Number.isNaN(start.getTime())) return null
  const { duration } = formatStartDate({ date: start })
  const { years, months } = duration
  if (years > 0 && months > 0) return `${years}y${months}m`
  if (years > 0) return `${years}yr`
  return `${months}m`
}
